import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmailPreviewModal = ({ campaign, message, isOpen, onClose, type = 'campaign' }) => {
  const [previewHtml, setPreviewHtml] = useState('');
  const [subject, setSubject] = useState('');
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && (campaign || message)) {
      loadPreview();
    }
  }, [isOpen, campaign, message, type]);

  const loadPreview = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      let response;
      
      if (type === 'message' && message) {
        // Preview de mensaje individual con datos reales
        response = await axios.post(
          `${API_URL}/api/email-campaigns/messages/${message.id}/preview`,
          {},
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        setRecipientInfo({
          name: response.data.recipient_name,
          email: response.data.recipient_email
        });
      } else if (type === 'campaign' && campaign) {
        // Preview de campaña con datos mock
        response = await axios.post(
          `${API_URL}/api/email-campaigns/preview`,
          {
            template_name: campaign.id,
            data_payload: {} // Vacío para usar datos mock
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
      }

      setPreviewHtml(response.data.html_content);
      setSubject(response.data.subject);
    } catch (error) {
      console.error('Error loading preview:', error);
      toast.error('Error al cargar preview');
    } finally {
      setLoading(false);
    }
  };

  if (!campaign && !message) return null;
  
  const displayName = type === 'message' 
    ? (message?.campaign_name || 'Mensaje')
    : (campaign?.name || 'Campaña');

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <div>
              <div className="text-lg font-semibold">{displayName}</div>
              {subject && (
                <div className="text-sm font-normal text-slate-600 mt-1">
                  Asunto: {subject}
                </div>
              )}
              {recipientInfo && (
                <div className="text-sm font-normal text-slate-500 mt-1">
                  Para: {recipientInfo.name} ({recipientInfo.email})
                </div>
              )}
            </div>
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden border border-slate-200 rounded-lg bg-white">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-slate-500">Cargando preview...</div>
            </div>
          ) : (
            <iframe
              srcDoc={previewHtml}
              title="Email Preview"
              className="w-full h-full"
              sandbox="allow-same-origin"
              style={{ minHeight: '500px' }}
            />
          )}
        </div>

        <div className="flex justify-between items-center pt-4 border-t">
          <div className="text-xs text-slate-500">
            {type === 'message' 
              ? 'Vista previa con datos reales del destinatario'
              : 'Vista previa con datos de ejemplo'
            }
          </div>
          <Button onClick={onClose} variant="outline">
            Cerrar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
